import { useRef, useState } from "react";
import {
  Alert,
  Button,
  InputText,
  SectionCard,
  Select,
  TextArea,
  type SelectOption,
  PageWrapper,
} from "@/deps/ui-library";
import { useEmailTemplates, useResetEmailTemplate, useSaveEmailTemplate } from "@/api/queries";
import type { EmailTemplate } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";

const LABELS: Record<string, string> = {
  Invoice: "Invoice mail",
  BalanceConfirmation: "Balance confirmation",
  Quotation: "Quotation",
  PurchaseOrder: "Purchase order",
  Reminder1: "Payment reminder: gentle",
  Reminder2: "Payment reminder: firm",
  Reminder3: "Payment reminder: final notice",
};

/** Made-up facts to preview with, so a person sees a message and not a list of braces. */
const SAMPLE: Record<string, string> = {
  customer_name: "Everest Distributors Pvt Ltd",
  supplier_name: "Softech Solutions Pvt Ltd",
  invoice_number: "INV-0248",
  quote_number: "QUO-0041",
  valid_until: "13 Sep 2026",
  amount: "1,003,225.00",
  currency: "NPR",
  due_date: "2 Sep 2026",
  period_end: "31 Ashad 2083",
  days_overdue: "32",
  company_name: "Your company",
};

const fill = (text: string) =>
  text.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, name: string) => SAMPLE[name] ?? match);

/** The wording of the mails a company sends. Notify puts it inside the company's envelope. */
export function EmailTemplatesPage() {
  const { data } = useEmailTemplates();
  const [kind, setKind] = useState("Invoice");
  const template = data?.find((t) => t.kind === kind);

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader title="Email templates" />
      <div className="md:w-1/2">
        <Select
          label="Template"
          options={(data ?? []).map((t) => ({ value: t.kind, label: LABELS[t.kind] ?? t.kind }))}
          value={kind}
          showClearButton={false}
          onChange={(details: { value: SelectOption | SelectOption[] | null }) => {
            const option = Array.isArray(details.value) ? details.value[0] : details.value;
            if (option) setKind(String(option.value));
          }}
        />
      </div>
      {/* A new key per template and per saved version, so the form starts from what's saved. */}
      {template ? (
        <TemplateForm key={`${kind}:${template.subject}:${template.body}`} template={template} />
      ) : null}
    </PageWrapper>
  );
}

function TemplateForm({ template }: { template: EmailTemplate }) {
  const save = useSaveEmailTemplate();
  const reset = useResetEmailTemplate();
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const bodyBox = useRef<HTMLTextAreaElement>(null);

  const changed = subject !== template.subject || body !== template.body;
  const complete = subject.trim() !== "" && body.trim() !== "";
  const error = save.error ?? reset.error;

  /** Puts a placeholder where the cursor is, and leaves the cursor after it. */
  const insert = (name: string) => {
    const token = `{{${name}}}`;
    const box = bodyBox.current;
    const start = box?.selectionStart ?? body.length;
    const end = box?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    setTimeout(() => {
      box?.focus();
      box?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <SectionCard title="Wording">
        <div className="flex flex-col gap-3">
          {error ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={(error as Error).message}
            />
          ) : null}
          <InputText
            label="Subject"
            required
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
          <div className="flex flex-wrap gap-1">
            {template.placeholders.map((name) => (
              <Button
                key={name}
                colorScheme="default"
                variant="outline"
                size="xs"
                aria-label={`Insert ${name}`}
                onClick={() => insert(name)}
              >
                {`{{${name}}}`}
              </Button>
            ))}
          </div>
          <TextArea
            ref={bodyBox}
            label="Body"
            required
            rows={12}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <span className="flex gap-2">
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={!changed || !complete || save.isPending}
              onClick={() => save.mutate({ kind: template.kind, subject, body })}
            >
              {save.isPending ? "Saving" : "Save"}
            </Button>
            {template.customised ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="sm"
                disabled={reset.isPending}
                onClick={() => reset.mutate(template.kind)}
              >
                Back to the default
              </Button>
            ) : null}
          </span>
        </div>
      </SectionCard>
      <SectionCard title="Preview">
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-foreground font-medium">{fill(subject)}</p>
          <p className="text-foreground whitespace-pre-line">{fill(body)}</p>
        </div>
      </SectionCard>
    </div>
  );
}
